import { getTotaltAntallMandater } from './components/shared/HasMandater/HasMandater'

export type Locale = 'nb' | 'nn' | 'sme'
export type Valgtype = 'ko' | 'fy' | 'st'

export type Valg = {
  valgKey: string
  antallValgdistrikter: number
  geografiKey?: string
}

export type Parti = {
  id: string
  navn: LocalizedName
  kortNavn: string
  sokbartKortNavn?: string
  kategori: number
  farge: string
  fargeSvak: string
  valg: Valg[]
}

export type Geografi = {
  key: string // Valgdir key
  navn: { nb: string; nn?: string; sme?: string }
  type: 'Nasjon' | 'Fylke' | 'Kommune' | 'Bydel' | 'Krets'
  bydel?: ParentGeografi
  parent?: ParentGeografi
  sammenslattAv?: LocalizedName[]
  nyttNavn?: { nb: string; nn?: string; sme?: string } // Kun brukt i søket
}

export interface GeografiMedStemmeinfo extends Geografi {
  innbyggertall?: number
  stemmeberettigede?: number
}

export function toNavType(geografi: Geografi) {
  switch (geografi.type) {
    case 'Fylke':
      return 'fylke'
    case 'Kommune':
      return 'kommune'
    case 'Bydel':
      return 'bydel'
    case 'Krets':
      return 'krets'
    default:
      throw new Error('Ukjent nav type: ' + geografi.type)
  }
}

export type PageNotFound = 'PAGE_NOT_FOUND'

export type PageType = Parti | Geografi | PageNotFound

export type ParentGeografi = {
  key: string // Valgdir key
  navn: LocalizedName
}

export function isGeografi(grunndata: PageType): grunndata is Geografi {
  return grunndata !== undefined && (<Geografi>grunndata).key !== undefined
}

export function isParti(grunndata: PageType): grunndata is Parti {
  return grunndata !== undefined && (<Parti>grunndata).kortNavn !== undefined
}

export function isPageNotFound(grunndata: PageType): grunndata is PageNotFound {
  return grunndata === 'PAGE_NOT_FOUND'
}

export type LocalizedName = { nb: string; nn?: string; sme?: string }

export type PartiResultat = {
  parti: Parti
  stemmer: {
    prosent?: number
    endring: {
      samme: number
    }
    antall?: {
      total: number
      fhs: number
    }
  }
  mandater?: {
    antall: number | undefined
    endring: number
    utjevningAntall: number
    nesteMandat: { kvotient: number; mandatrang: number }
    sisteMandat?: { kvotient: number; mandatrang: number }
  }
}

export interface PartiResultatMedMandat extends PartiResultat {
  mandater: {
    antall: number | undefined
    endring: number
    utjevningAntall: number
    nesteMandat: { kvotient: number; mandatrang: number }
    sisteMandat: { kvotient: number; mandatrang: number }
  }
}

export type MandatInfo = {
  antall: number
  endring: number
  nesteMandat: string
  sisteMandat: string
}

export type StemmeInfo = {
  total: number
  fhs: number
  totalForkastede: number
  fhsForkastede: number
}

export type FlertallInfo = {
  partier: string[]
  antallMandater: number
  antallMandaterOvrige: number
}

export type OpptaltInfo = {
  prosent: number
  forelopigFhs: number
  forelopigVts: number
  forelopig: number
  endelig: number
  oppgjor: number
}

export type FremmoteInfo = {
  prosent: number
  endring: number
  prosentStemmegiving: number
}

export type ValgResultat = {
  type: 'valgresultat'
  resultsLocked?: boolean
  resultatType: 'Prognose' | 'Resultat' | 'PrognoseOverstyring'
  valg?: {
    year: number
    type: Valgtype
  }
  geografi: Geografi
  tidspunkt: {
    rapportGenerert: string
    sisteStemmer: string
  }
  antallsb: number
  mandater?: MandatInfo
  stemmer?: StemmeInfo
  stemmegiving: {
    totalGodkjente: number
    fhsGodkjente: number
    totalForkastede: number
    fhsForkastede: number
  }
  frammote?: FremmoteInfo
  opptalt?: OpptaltInfo
  /*
  prognose: {
    beregnet: boolean
    historiskTotalavvik: any | null
  }
  */
  flertall?: FlertallInfo
  partier?: PartiResultat[]
  pastander: any
}

export type GeografiskResultat = {
  geografi: Geografi
  resultatType: string
  stemmer: {
    prosent?: number
    endring: {
      samme: number
    }
    antall: {
      total: number
      fhs: number
    }
  }
  mandater: {
    antall: number
    endring: number
    utjevningAntall: number
    sisteMandat?: {
      kvotient: number
      mandatrang: number
    }
    nesteMandat: {
      kvotient: number
      mandatrang: number
    }
    utjevning?: {
      kvotient: number
      mandatrang: number
    }
    representanter: [
      {
        navn: string
        kjonn: string
        alder: number
        utjevningsmandat: true
      }
    ]
  }
  listetopp: {
    navn: string
    bildeUrl: string
  }
  lastUpdated: string
}

export type PartiValgResultat = {
  type: 'partivalgresultat'
  resultsLocked?: boolean
  parti: Parti & {
    kandidater: string[]
  }
  steder: GeografiskResultat[]
  nasjonalt?: GeografiskResultat
}

export function isValgResultat(
  resultat: ValgResultat | PartiValgResultat
): resultat is ValgResultat {
  return resultat !== undefined && (<ValgResultat>resultat).type === 'valgresultat'
}

export function isPartiValgResultat(
  resultat: ValgResultat | PartiValgResultat
): resultat is PartiValgResultat {
  return resultat !== undefined && (<PartiValgResultat>resultat).type === 'partivalgresultat'
}

export type FetchType = 'LOAD' | 'RELOAD'

export type FetchError = {
  error: Error
  grunndata: Geografi | Parti
}
